/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import React, { useCallback, useEffect, useRef } from 'react';

import { useAppDispatch, useAppSelector } from '../../../app/hooks';
// eslint-disable-next-line import/no-restricted-paths
import { FhirSidebarInnerFlippable } from '../../FHIRFlipped/FhirSidebarInnerFlippable';
import { selectCreatorMode } from '../chat/chatSlice';
import { useFhirScopeIfExists, useMessageIndex } from '../chat/hooks/MessageContext';
import { useScrollWithShadow } from '../chat/useHooks';
import { useEditFhirUserFlag, useTellUsAboutYourExperienceBannerUserFlag } from '../permissions';
import styles from './FhirSidebar.module.scss';
import { fhirUpdatePositions } from './FhirSidebar_utils';
import { FhirSidebarInner } from './FhirSidebarInner';
import { selectFhirHighlight, selectIsFhirVisible, setCurrentlyHighlightedTop, setIsFhirVisible } from './fhirSlice';
import { selectPatientSummaryState } from '../chat/components/PatientSummary/patientSummarySlice';
import { PageFhirType } from '../algorithm/types';
import { FhirCallout } from '../chat/FhirCallout';
import { useThrottle } from '../utils/customHooks';
import { useIsMounted } from '../useHooks';
import { useHtmlFhirForMessage } from './useHooksFhirSidebar';
import { CollapsibleSidebar } from '../chat/components/CollapsibleSidebar/CollapsibleSidebar';

export enum FhirSidebarType {
  Labs = 'Labs',
  NonLabs = 'NonLabs',
  All = 'All',
}

interface FhirSidebarProps {
  className?: string;
  documentVersionId?: string;
  readOnly: boolean;
  compact?: boolean;
  noPatient?: boolean;
  inEditorMode?: boolean;
  isInGuide?: boolean;
  onChangeFhir?: (new_fhir: PageFhirType, pageId: string) => void;
  calloutOffsetX?: number;
  calloutOffsetY?: number;
}

/**
 * This shows the sidebar with the FHIR data on the side.
 * When the user hovers over a FHIR widget or over a word in the document, FhirHighlight is used to highlight the widget and the word,
 * and FhirCallout draws a line between the two.
 *
 * To get the FHIR data, this component uses the htmlFhir state from the fhirSlice.
 *  htmlFhir is the list of <fhir> tags we found in the current page, and it's set by
 *  `setListOfHtmlFhir` in `useUpdateFhirSidebar()` in `RenderedHTML` (for "non-flipped" mode)
 *  and Editor.tsx (for "flipped" mode).
 *  The actual widget id for the FHIR data is in the page.fhir object.
 */
export function FhirSidebar({
  className,
  compact,
  readOnly,
  documentVersionId,
  noPatient,
  inEditorMode,
  isInGuide,
  onChangeFhir,
  calloutOffsetX,
  calloutOffsetY,
}: FhirSidebarProps) {
  const combineSamePredefinedWidget = !inEditorMode; // TODO - make parameter?
  //const html = htmlRef?.current;
  const currentlyHighlighted = useAppSelector(selectFhirHighlight);
  const dispatch = useAppDispatch();
  const messageIndex = useMessageIndex();
  const fhirScope = useFhirScopeIfExists();
  /* htmlFhir is the list of <fhir> tags we found in the current page or in a button.
     It's set by setListOfHtmlFhir in useUpdateFhirSidebar() in
     RenderedHTML or by Editor.tsx
     If two tags (or buttons) point to the same predefined widget, they are combined into one.
     A tag can also point to multiple predefined widgets, in which case we show them all.
     */
  const { htmlFhirConfig, htmlFhirConfigUngrouped, validFhirExists } = useHtmlFhirForMessage(
    messageIndex,
    combineSamePredefinedWidget,
    !!inEditorMode
  );
  const editFhirUserFlag = useEditFhirUserFlag();
  const containerRef = useRef<HTMLDivElement>(null);
  const isCreatorMode = useAppSelector(selectCreatorMode);
  const { showTopic } = useAppSelector(selectPatientSummaryState);
  const { scroll, onScrollHandler: scrollWithShadowCb } = useScrollWithShadow();
  const isFhirVisible = useAppSelector(selectIsFhirVisible);
  const isMessagebar = useTellUsAboutYourExperienceBannerUserFlag();

  // Keep track of whether we are still mounted, since we have setTimeout's here
  const mounted = useIsMounted();

  const _update = useCallback(() => {
    // Check we are still mounted, since we call this function after a timeout
    if (!mounted.current) {
      return;
    }
    const isHighLightedWidgetExists =
      currentlyHighlighted &&
      currentlyHighlighted.fhirWidgetIds &&
      htmlFhirConfig.filter((x) => x.fhirWidgetIds.includes(currentlyHighlighted.fhirWidgetIds![0]!)).length > 0;
    const { currentlyHighlightedTop } = fhirUpdatePositions(
      htmlFhirConfig,
      messageIndex,
      fhirScope?.scopeRef.current ??
        (document.querySelector(`[data-message-index="${messageIndex}"]`) as HTMLElement | null),
      !!inEditorMode,
      !!isInGuide,
      showTopic,
      isHighLightedWidgetExists ? currentlyHighlighted?.fhirWidgetIds : undefined
    );

    // using debounce, to minimize the redux state updates
    if (isHighLightedWidgetExists) {
      dispatch(setCurrentlyHighlightedTop(currentlyHighlightedTop));
    }
    window.dispatchEvent(new CustomEvent('fhirSidebarScroll'));
  }, [
    currentlyHighlighted,
    dispatch,
    fhirScope?.scopeRef,
    htmlFhirConfig,
    inEditorMode,
    isInGuide,
    messageIndex,
    mounted,
    showTopic,
  ]);

  const _doUpdate = useCallback(() => {
    _update();
    // This is ugly, but sometimes we need to wait a bit of time before the sizes are exactly right.  I don't know why.
    // _update will check if we're still mounted.
    setTimeout(() => _update(), 0);
    setTimeout(() => _update(), 300);

    setTimeout(() => mounted.current && scrollWithShadowCb({ target: containerRef.current }), 400);
  }, [_update, mounted, scrollWithShadowCb]);

  /** Move all the widgets down so that they do not overlap with each other.
   * Individual widgets can request a relayout while mounting, so coalesce those requests.
   */
  const doUpdate = useThrottle(_doUpdate, 100, false);

  const onScrollHandler = useCallback(
    (event: React.UIEvent<HTMLDivElement> | { target: HTMLDivElement }) => {
      // Dispatch an event to let FhirCallout know that the sidebar has scrolled
      window.dispatchEvent(new CustomEvent('fhirSidebarScroll'));
      scrollWithShadowCb(event);
    },
    [scrollWithShadowCb]
  );

  useEffect(() => {
    void doUpdate();
  }, [doUpdate, htmlFhirConfig, messageIndex, showTopic]);

  useEffect(() => {
    window.addEventListener('resize', doUpdate);
    window.addEventListener('actionClick', doUpdate);
    window.addEventListener('fhirRemoved', doUpdate);
    return () => {
      window.removeEventListener('resize', doUpdate);
      window.removeEventListener('actionClick', doUpdate);
      window.removeEventListener('fhirRemoved', doUpdate);
    };
  }, [doUpdate]);

  // in consumer mode, we have to show fhir sidebar container in all pages if fhir configured for atleast one page
  // so, we are checking valid fhir exists in atleast one page and setting isFhirVisible to true
  // if fhir is not configured in none of the pages, then hide fhir sidebar container
  useEffect(() => {
    if (!isCreatorMode) {
      dispatch(setIsFhirVisible(validFhirExists));
    }
  }, [validFhirExists, dispatch, isCreatorMode]);

  if (!isCreatorMode && !isFhirVisible) return null;

  if (documentVersionId) {
    if (inEditorMode && htmlFhirConfig.length === 0) {
      return null;
    }
    if (inEditorMode && !onChangeFhir) {
      throw new Error('In Editor mode you need to pass onChangeFhir');
    }

    const sideBarParentClassName = `${className} ${styles.container} ${compact || noPatient ? styles.compact : ''}`;
    const fhirSidebarInnerFlippable =
      !!FhirSidebarInnerFlippable && onChangeFhir && htmlFhirConfig?.length ? (
        <FhirSidebarInnerFlippable
          key={messageIndex}
          messageIndex={messageIndex}
          readOnly={readOnly || !editFhirUserFlag}
          htmlFhirConfig={htmlFhirConfig}
          htmlFhirConfigUngrouped={htmlFhirConfigUngrouped}
          onUpdate={doUpdate}
          onChangeFhir={onChangeFhir}
          documentVersionId={documentVersionId}
          compact={compact}
          noPatient={noPatient}
          inEditorMode={inEditorMode}
        />
      ) : null;

    if (!validFhirExists && !fhirSidebarInnerFlippable) {
      return null;
    }
    const sidebar = (collapseButton?: React.ReactNode) => (
      <>
        <FhirCallout xOffset={calloutOffsetX} yOffset={calloutOffsetY} />
        <div
          className={`fhir-sidebar ${styles.relativeContainer}
            ${compact || noPatient ? styles.compact : ''}
            ${!!isInGuide ? styles.guideContainer : ''}`}
          data-scroll={scroll} /* 'none', 'both', 'top', 'bottom'. If we are scrolled to the top etc. */
          data-private
        >
          {validFhirExists ? (
            <>
              {collapseButton}
              <div
                ref={containerRef}
                onScroll={onScrollHandler}
                className={styles.scrollContainer}
                data-iscreator={isCreatorMode}
                data-isinguide={!!isInGuide}
                data-ismessagebar={isMessagebar}
                id="fhir-scroll-container"
              >
                {!!FhirSidebarInnerFlippable && onChangeFhir ? (
                  <div className={sideBarParentClassName}>{fhirSidebarInnerFlippable}</div>
                ) : (
                  <>
                    <div className={styles.containerPane}>
                      <div className={sideBarParentClassName}>
                        <FhirSidebarInner
                          messageIndex={messageIndex}
                          readOnly={readOnly}
                          htmlFhirConfig={htmlFhirConfig}
                          onUpdate={doUpdate}
                          documentVersionId={documentVersionId}
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
              <div
                className={styles.scrollIndicator}
                onClick={(e) => {
                  // Smooth scroll down by a bit
                  const container = containerRef.current;
                  if (!container) return;
                  container.scrollBy({ top: 200, behavior: 'smooth' });
                  onScrollHandler({ target: container });
                }}
              />
            </>
          ) : (
            fhirSidebarInnerFlippable
          )}
        </div>
      </>
    );

    if (!isCreatorMode) return sidebar();

    return (
      <CollapsibleSidebar
        collapsedLabel="Contextualization"
        collapseButtonClassName={styles.collapseButton}
        collapseTitle="Collapse Contextualization Sidebar"
        expandButtonClassName={styles.expandButton}
        expandTitle="Expand Contextualization Sidebar"
        storageKey="fhirSidebar.collapsed"
      >
        {(collapseButton) => sidebar(collapseButton)}
      </CollapsibleSidebar>
    );
  } else {
    return null;
  }
}
